import {
  escapeRegExp,
  get,
  memoize,
  filter,
  uniqBy,
  sortBy,
  map,
  reduce,
  uniq,
  cloneDeep,
} from 'lodash'

import {
  LocationsInfo,
  KEY_LOCATIONS_INFO,
} from '../state/actions/locationManager'

import AllLocations from './locations.json'
import AllLanguages from './languages.json'
import configLocLangs from './configLocLangs.json'
import AllLocales from './locales.json'
import AllCountries from './countriesConfig.json'
import AllCurrencies from './currencies.json'
import { translate, getLanguageCode } from './translation'

export const GlobalLocationInfo = {
  id: '0000',
  name: 'All Locations',
  parentId: null,
  type: 'All',
  text: translate('all_locations'),
  languageCode: 'WW',
  countryIsoCode: 'WW',
  canonicalName: translate('all_locations'),
}

export const InvalidLocation = {
  id: '',
  name: 'invalid_location',
  parentId: null,
  type: 'invalid',
  text: 'invalid_location',
  canonicalName: 'invalid_location',
  countryIsoCode: 'invalid_location',
}

export const {
  DEFAULT_BAN_COUNTRIES,
  DFS_NOT_AVAILABLE_LOC_LANGS,
  DEFAULT_BAN_LANGS,
  COMPETITOR_LOC_LANGS,
  SEARCH_INTENT_LANGS,
} = configLocLangs
export const DEFAULT_TOP_5_COUNTRIES = [
  'en/US',
  'en/GB',
  'ja/JP',
  'en/AU',
  'en/CA',
]
export const isCountry = (locId) =>
  locId && AllLocations[locId] ? AllLocations[locId].type === 'Country' : false

export const isGlobal = (langLoc) =>
  /^(0000).*|^.*(0000)$/.test(`${langLoc}`) ||
  /^(WW).*|^.*(WW)$/.test(`${langLoc}`) ||
  /^(All).*|^.*(All)$/.test(`${langLoc}`)

export const isCity = (locId) => locId && !isCountry(locId) && !isGlobal(locId)

export const getCountryIsoCode = (locId, cachedLocations = {}) => {
  let country = AllLocations[locId]

  if (!country) {
    country = cachedLocations[locId]
      ? AllLocations[cachedLocations[locId].parent_id]
      : null
  }

  return country ? country.countryIsoCode : null
}

export const regTestWithTranslation = (regExps, canonicalName, lang) =>
  regExps.every(
    (reg) =>
      reg.test(translate(canonicalName).split(',')[0]) ||
      reg.test(translate(lang)),
  )

export const isLanguageBanned = (lang, banList) => {
  if (!banList) return false
  return banList.some((item) => item === lang)
}

export const getAvailableLanguages = memoize(
  (countryIsoCode, isCompetitorLocLangs = false) => {
    if (countryIsoCode === 'WW') {
      return AllLanguages
    }
    if (AllLanguages[countryIsoCode]) {
      if (isCompetitorLocLangs) {
        const locId = String(getCountryInfo(countryIsoCode).id)
        return uniqBy(AllLanguages[countryIsoCode], (item) => item.code).filter(
          (lang) => COMPETITOR_LOC_LANGS[locId].some((bl) => bl === lang.code),
        )
      }
      return uniqBy(AllLanguages[countryIsoCode], (item) => item.code).filter(
        (lang) => !DEFAULT_BAN_LANGS.some((bl) => bl === lang.code),
      )
    }
    return []
  },
  (...args) => JSON.stringify(args),
)

export const hasLanguage = memoize(
  (countryIsoCode, language, isCompetitorLocLangs = false) => {
    if (countryIsoCode === 'WW') return true
    const languages = getAvailableLanguages(
      countryIsoCode,
      isCompetitorLocLangs,
    )
    const result = languages?.some((l) => l.code === language)
    return result
  },
  (...args) => JSON.stringify(args),
)

export const getAllLangLocCombinations = memoize(() => {
  const locs = {}
  Object.values(AllLocations).forEach((location) => {
    if (
      location.countryIsoCode in AllLanguages &&
      !DEFAULT_BAN_COUNTRIES.some((c) => c === `${location.id}`)
    ) {
      const locationLanguages = AllLanguages[location.countryIsoCode]
      locationLanguages.forEach((language) => {
        if (DEFAULT_BAN_LANGS.some((l) => l === language.code)) return

        locs[`${language.code}_${location.id}`] = {
          text:
            location.type === 'Country'
              ? `${language.name} / ${location.name}`
              : `${language.name} / ${location.canonicalName}`,
          languageName: language.name,
          languageCode: language.code,
          ...location,
        }
      })
    }
  })
  return locs
})

export const getCompetitorLangLocCombinations = memoize(() => {
  const locs = {}

  map(COMPETITOR_LOC_LANGS, (languages, locId) => {
    languages.forEach((lang) => {
      const location = AllLocations[locId]
      const language = AllLanguages[location.countryIsoCode].find(
        (l) => l.code === lang,
      )
      locs[`${lang}_${locId}`] = {
        text:
          location.type === 'Country'
            ? `${language.name} / ${location.name}`
            : `${language.name} / ${location.canonicalName}`,
        languageName: language.name,
        languageCode: language.code,
        ...location,
      }
    })
  })

  return locs
})

export const isValidLangLoc = (lang, locId, isCompetitor = false) => {
  if (isCompetitor) return isValidCompetitorLangLoc(lang, locId)

  return (
    !DEFAULT_BAN_COUNTRIES.some((bLocId) => bLocId === locId) &&
    !DEFAULT_BAN_LANGS.some((bLang) => bLang === lang)
  )
}

export const isValidCompetitorLangLoc = (lang = '', locId = '') => {
  return (COMPETITOR_LOC_LANGS[`${locId}`] || []).some(
    (cLang) => cLang === lang,
  )
}

export const getAllLocations = memoize((banLocs = []) => {
  return Object.values(AllLocations).filter(
    (location) =>
      location.countryIsoCode in AllLanguages &&
      !banLocs.some((locId) => locId === `${location.id}`),
  )
})

export const getLocale = (lang, locId, cachedLocations = {}) => {
  const isoCode = getCountryIsoCode(locId, cachedLocations)
  const countryInfo = AllCountries[isoCode.toLowerCase()]
  const defaultCountryLocale = {
    currency: countryInfo.currency,
    decimalSeparator: countryInfo.decimalSeparator,
    thousandSeparator: countryInfo.thousandSeparator,
  }
  // Return default locale of location if we don't have locale for specific language
  return AllLocales[`${lang}_${isoCode}`] || defaultCountryLocale
}

export const getParentLocId = (locId, cachedLocations = {}) => {
  if (locId) {
    if (isGlobal(locId) || AllLocations[locId]?.type === 'Country') {
      return locId
    }
    return cachedLocations[locId]?.parent_id || 2840
  }

  return 2840
}

export const getAllLanguageCodes = memoize(
  (supportedLangs, banLangs = DEFAULT_BAN_LANGS) => {
    const langSet = new Set()
    if (supportedLangs)
      supportedLangs.forEach((lang) => {
        if (!banLangs.some((l) => l === lang)) langSet.add(lang)
      })
    else {
      Object.values(AllLanguages).forEach((locale) => {
        locale.forEach((entry) => {
          if (!banLangs.some((l) => l === entry.code)) langSet.add(entry.code)
        })
      })
    }
    return sortBy(Array.from(langSet), (l) => translate(l))
  },
)

export const getLocationInfo = (
  locId,
  cachedLocations,
  defaultValue = InvalidLocation,
) => {
  if (isGlobal(locId)) return GlobalLocationInfo

  if (AllLocations[locId]) return AllLocations[locId]

  const cachedLocation = getCachedLocationById(locId, cachedLocations)
  if (cachedLocation) {
    return {
      id: cachedLocation.id,
      parentId: cachedLocation.parent_id,
      name: cachedLocation.name,
      type: 'City',
      countryIsoCode: AllLocations[cachedLocation.parent_id]?.countryIsoCode,
      canonicalName: cachedLocation.name,
    }
  }

  return defaultValue
}

export const getCountryInfo = memoize(
  (countryCode) => {
    const country =
      Object.values(AllLocations).find(
        (item) =>
          item.countryIsoCode.toLowerCase() === countryCode.toLowerCase(),
      ) || AllLocations[2840] // use US as default
    const languageCode = get(
      AllLanguages,
      [countryCode.toUpperCase(), 0, 'code'],
      'en',
    ) // use English as default

    return {
      ...country,
      locationId: country.id,
      languageCode,
    }
  },
  (...args) => JSON.stringify(args),
)

export const removeLastPart = (canonicalName) => {
  const splits = canonicalName.split(',')
  splits.splice(splits.length - 1, 1)
  return splits.join(',')
}

export const getNewLangs = () => {
  const numbroCountries = {}
  Object.values(AllCountries).forEach((countryInfo) => {
    const countryIsoCode = getCountryIsoCode(countryInfo.locationId)
    if (countryIsoCode in AllLanguages) {
      const locationLanguages = AllLanguages[countryIsoCode]
      locationLanguages.forEach((language) => {
        const lang = language.code.substring(0, 2)
        const languageTag = `${lang}-${countryIsoCode}`
        const locale = getLocale(lang, countryInfo.locationId)

        numbroCountries[languageTag] = {
          languageTag,
          delimiters: {
            thousands: locale?.thousandSeparator,
            decimal: locale?.decimalSeparator,
          },
          currency: {
            symbol: locale?.currency,
            code: AllCurrencies[countryIsoCode]?.currency || '',
            position: AllCurrencies[countryIsoCode]?.position || 'prefix',
          },
          abbreviations: {
            thousand: 'k',
            million: 'm',
            billion: 'b',
            trillion: 't',
          },
          ordinal() {
            return '.'
          },
        }
      })
    }
  })
  return numbroCountries
}

export const filterCompetitorsLocations = ({
  locations = [],
  cachedLocations = {},
}) => {
  const pLocations = locations
    .map((item) => ({
      loc_id:
        getParentLocId(item.loc_id || item.id, cachedLocations) ||
        item.loc_id ||
        item.id,
      lang: item.languageCode || item.lang,
    }))
    .filter((item) => isValidCompetitorLangLoc(item.lang, item.loc_id))
  if (pLocations.length === 0) {
    // Add English / US as default
    pLocations.push({
      loc_id: 2840,
      lang: 'en',
    })
  }
  return uniqBy(pLocations, (item) => `${item.loc_id}${item.lang}`).map(
    (loc) => ({
      ...loc,
      ...getLocationInfo(loc.loc_id),
      languageCode: loc.lang,
    }),
  )
}

export const getDFSAvailableLocations = memoize(
  () => {
    const locations = getAllLangLocCombinations()

    let filteredOptions
    filteredOptions = filter(locations, (l) => l.type === 'Country')

    filteredOptions = filter(
      filteredOptions,
      (c) =>
        !isLanguageBanned(
          c.languageCode,
          DFS_NOT_AVAILABLE_LOC_LANGS[`${c.id}`],
        ),
    )

    return filteredOptions
  },
  (...args) => JSON.stringify(args),
)

export const getLocations = memoize(
  (allowCities, banLocLangs = {}, enableAllLocations = false) => {
    const locations = getAllLangLocCombinations()
    let filteredOptions
    if (allowCities) {
      filteredOptions = filter(
        locations,
        (c) => !isLanguageBanned(c.languageCode, get(banLocLangs, `${c.id}`)),
      )
    } else {
      filteredOptions = filter(
        locations,
        (l) =>
          l.type === 'Country' &&
          !isLanguageBanned(l.languageCode, get(banLocLangs, `${l.id}`)),
      )
    }

    filteredOptions = sortBy(filteredOptions, [
      (l) => (l.type === 'Country' ? 0 : 1),
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[0]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[1]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[2]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[3]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[4]
          ? 0
          : 1,
      'name',
      'languageName',
    ])
    if (enableAllLocations) {
      filteredOptions.unshift(GlobalLocationInfo)
    }
    return filteredOptions
  },
  (...args) => JSON.stringify(args),
)

export const getCompetitorLocations = memoize(
  (search) => {
    const locations = getCompetitorLangLocCombinations()
    const regExps = (search || '')
      .split(' ')
      .filter((searchWord) => searchWord.trim().length)
      .map((searchWord) => new RegExp(escapeRegExp(searchWord) || '.*.', 'i'))

    let filteredOptions
    filteredOptions = filter(locations, (l) => l.type === 'Country')

    filteredOptions = filter(filteredOptions, (c) =>
      regTestWithTranslation(regExps, c.canonicalName, c.languageCode),
    )

    filteredOptions = sortBy(filteredOptions, [
      (l) => (l.type === 'Country' ? 0 : 1),
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[0]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[1]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[2]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[3]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[4]
          ? 0
          : 1,
      'name',
      'languageName',
    ])
    return filteredOptions
  },
  (...args) => JSON.stringify(args),
)

export const getCityLocations = memoize(
  (data, banLocLangs = {}) => {
    const cities = {}

    data.forEach((location) => {
      const country = AllLocations[location.parent_id]

      const locationLanguages = AllLanguages[country.countryIsoCode]
      locationLanguages.forEach((language) => {
        if (DEFAULT_BAN_LANGS.some((l) => l === language.code)) return

        cities[`${language.code}_${location.id}`] = {
          id: location.id,
          parentId: location.parent_id,
          name: location.name,
          type: location.is_country ? 'Country' : 'City',
          countryIsoCode: country.countryIsoCode,
          canonicalName: location.name,
          text: `${language.name} / ${location.name}`,
          languageName: language.name,
          languageCode: language.code,
        }
      })
    })

    let filteredOptions
    filteredOptions = filter(
      cities,
      (c) => !isLanguageBanned(c.languageCode, banLocLangs[`${c.id}`]),
    )

    filteredOptions = sortBy(filteredOptions, [
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[0]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[1]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[2]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[3]
          ? 0
          : 1,
      (l) =>
        `${l.languageCode}/${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[4]
          ? 0
          : 1,
      'name',
      'languageName',
    ])

    return filteredOptions
  },
  (...args) => JSON.stringify(args),
)

export const generateNewLangLocOptions = memoize(
  (data) => {
    return data.map((location) => {
      const country = AllLocations[location.parent_id]

      return {
        id: location.id,
        parentId: location.parent_id,
        name: location.name,
        en: location.en,
        type: location.is_country ? 'Country' : 'City',
        countryIsoCode: country.countryIsoCode,
        canonicalName: location.name,
      }
    })
  },
  (...args) => JSON.stringify(args),
)

export const generateLangLocOptions = memoize(
  (allowCities, enableAllLocations = false, supportedLangs = null) => {
    const banLocations = DEFAULT_BAN_COUNTRIES
    const locations = getAllLocations(banLocations)
    const languageCodes = getAllLanguageCodes(supportedLangs)
    let filteredOptions
    if (allowCities) {
      filteredOptions = locations
    } else {
      filteredOptions = filter(locations, (l) => l.type === 'Country')
    }
    filteredOptions = sortBy(filteredOptions, [
      (l) => (l.type === 'Country' ? 0 : 1),
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[0].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[1].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[2].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[3].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[4].split('/')[1]
          ? 0
          : 1,
      'name',
      'languageName',
    ])

    if (enableAllLocations) {
      filteredOptions.unshift(GlobalLocationInfo)
    }
    return { languages: languageCodes, locations: filteredOptions }
  },
  (...args) => JSON.stringify(args),
)

export const generateCompetitorLangLocOptions = memoize(
  () => {
    // get Competitor locations
    const locations = filter(
      AllLocations,
      (location, locId) =>
        !!COMPETITOR_LOC_LANGS[locId] &&
        location.countryIsoCode in AllLanguages,
    )
    const supportedLangs = uniq(
      reduce(
        COMPETITOR_LOC_LANGS,
        (result, langs) => {
          return result.concat(langs)
        },
        [],
      ),
    )

    const languageCodes = getAllLanguageCodes(supportedLangs, []) // Do not ban default banned languages for competitors
    const filteredOptions = sortBy(locations, [
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[0].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[1].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[2].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[3].split('/')[1]
          ? 0
          : 1,
      (l) =>
        `${l.countryIsoCode}` === DEFAULT_TOP_5_COUNTRIES[4].split('/')[1]
          ? 0
          : 1,
      'name',
      'languageName',
    ])

    return { languages: languageCodes, locations: filteredOptions }
  },
  (...args) => JSON.stringify(args),
)

export const getDomainZones = () => Object.keys(AllCountries).sort()

export const mergeLocationsInfo = (origin, newLocations) => {
  const locations = cloneDeep(origin)
  const lang = getLanguageCode()
  newLocations.forEach((location) => {
    locations[location.id] = {
      ...locations[location.id],
      id: location.id,
      parent_id: location.parent_id,
      is_country: location.is_country,
      [lang]: location.name,
      ...(lang !== 'en' && {
        en: location.en,
      }),
    }
  })

  return locations
}

export const getCachedLocations = () => {
  const cachedLocations = LocationsInfo.getItem(KEY_LOCATIONS_INFO)
  if (cachedLocations) return JSON.parse(cachedLocations)
  return {}
}

export const getCachedLocationById = (locId, cachedLocations = {}) => {
  const lang = getLanguageCode()
  const location = cachedLocations[locId]
  if (location) {
    return {
      id: location.id,
      parent_id: location.parent_id,
      is_country: location.is_country,
      name: location[lang] ? location[lang] : location.en,
    }
  }
}

export const getInitialLangLocId = (tempLang, tempLocId) => {
  const lang = getAllLanguageCodes().includes(tempLang) ? tempLang : 'en'
  const locId = getAllLocations().some(
    (location) => location.id === Number(tempLocId),
  )
    ? tempLocId
    : '2840'

  return { lang, locId }
}

export const getCountryCode = (response) => {
  if (!response || !response.data) {
    return 'US'
  }

  const { data } = response

  return !DEFAULT_BAN_COUNTRIES.some(
    (countryCode) => countryCode === data.countryCode,
  )
    ? data.countryCode
    : 'US'
}

const zendeskMapLanguage = {
  en: 'en',
  pt: 'pt-br',
  de: 'de',
  es: 'es-es',
  fr: 'fr-fr',
  nl: 'nl-nl',
  it: 'it',
  ja: 'ja',
  zh: 'zh-cn',
}

export const setZendeskLocation = () => {
  if (!window.zE) {
    return
  }

  const language = getLanguageCode()

  window.zE('webWidget', 'setLocale', zendeskMapLanguage[language])
}
